/**
 * Menu flow state shared between screens (Main Menu → Mode Select → Character Select → Versus → match) and the
 * pure builder that turns it into a `MatchSetup`. Lives outside the store because it is UI-only bookkeeping.
 */
import { signal } from '@preact/signals-core';
import {
  CHARACTER_IDS,
  defaultMatchConfig,
  Difficulty,
  MAX_WIND,
  MatchFormat,
  SpecialsMode,
  type CharacterId,
  type GameMode,
  type PlayerIndex,
  type TimeOfDay,
} from '@hf/shared';
import { DEVICE_ID } from '../input/types';
import type { MatchSetup, SlotSetup } from './types';
import { graduated, progress, type TutorialProgress } from './tutorial/progress';

export type OfflineMode = Extract<GameMode, 'cpu' | 'local' | 'practice' | 'tutorial'>;
export type WindSetting = 'calm' | 'breezy' | 'gusty';
/** Stadium lighting choice: a fixed day / night game, or 'auto' (derived from the match seed, as before). */
export type VenueSetting = 'auto' | TimeOfDay;
export type LobbyTab = 'quick' | 'ranked' | 'private' | 'leaderboard';

export interface MatchDraft {
  mode: OfflineMode;
  /** DRIVES (default) or CLASSIC (casual: 2 halves × 2:00 with kickoff / punt / onside tosses). */
  format: MatchFormat;
  /** Stadium lighting. */
  venue: VenueSetting;
  drives: number;
  specials: SpecialsMode;
  wind: WindSetting;
  difficulty: Difficulty;
  characters: [CharacterId, CharacterId];
}

export const DEFAULT_DRAFT: MatchDraft = {
  mode: 'cpu',
  format: MatchFormat.DRIVES,
  venue: 'auto',
  drives: 4,
  specials: SpecialsMode.SIGNATURE,
  wind: 'calm',
  difficulty: Difficulty.NORMAL,
  characters: ['rookie', 'zorg'],
};

/**
 * The CPU difficulty an Exhibition starts on: EASY until the player has graduated Football 101 (Rookie Camp's
 * "play a game" promises the easy CPU, and a 0-25 loss to NORMAL is a rough first game), NORMAL after.
 */
export const defaultDifficulty = (p: TutorialProgress = progress.peek()): Difficulty =>
  graduated(p) ? Difficulty.NORMAL : Difficulty.EASY;

export const draft = signal<MatchDraft>({ ...DEFAULT_DRAFT, difficulty: defaultDifficulty() });
export const updateDraft = (patch: Partial<MatchDraft>): void => {
  draft.value = { ...draft.value, ...patch };
};

/**
 * The match the Versus splash is about to start (set by Character Select / the lobby, consumed by Versus, which
 * calls `actions.startMatch`). `venue` is the chyron line ("HEAD BOWL STADIUM · NIGHT GAME · RANKED").
 */
export interface PendingMatch {
  setup: MatchSetup;
  venue: string;
  /** Per-side subtitle under the name banners ("P1 · ALL-PRO · ELO 1,842", "CPU · HARD"). */
  tags: [string, string];
}
export const pendingMatch = signal<PendingMatch | null>(null);

/** Online lobby: which tab the main menu opened (ONLINE → quick, RANKED → ranked) and the chosen head. */
export const lobbyTab = signal<LobbyTab>('quick');
export const onlineCharacter = signal<CharacterId>('rookie');

/** Wind magnitude for a setting; the sign comes from the seed so both directions are equally likely. */
export const windValue = (w: WindSetting, seed: number): number => {
  const mag = w === 'calm' ? 0 : w === 'breezy' ? MAX_WIND * 0.4 : MAX_WIND * 0.85;
  return seed & 2 ? mag : -mag;
};

export const DRIVE_CHOICES = [2, 3, 4, 6, 8] as const;
export const DIFFICULTIES: readonly Difficulty[] = [
  Difficulty.EASY,
  Difficulty.NORMAL,
  Difficulty.HARD,
  Difficulty.ALL_PRO,
];

export const randomCharacter = (rand: () => number = Math.random): CharacterId =>
  CHARACTER_IDS[Math.floor(rand() * CHARACTER_IDS.length) % CHARACTER_IDS.length]!;

/**
 * Build the offline MatchSetup from the draft. Device ids: one human → keyboard (both layouts) + pad 0; two humans
 * → left keyboard half + pad 0 / right half + pad 1 (unknown pads are ignored by `InputManager.assign`).
 */
export const buildOfflineSetup = (d: MatchDraft, seed: number, playerName: string): MatchSetup => {
  const firstPossession = (seed & 1) as PlayerIndex;
  const tutorial = d.mode === 'tutorial';
  const practice = d.mode === 'practice';
  const config = defaultMatchConfig({
    seed,
    // Tutorial and practice drill the Drives rules; Classic is a casual option of real matches.
    format: tutorial || practice ? MatchFormat.DRIVES : d.format,
    ...(d.venue !== 'auto' ? { timeOfDay: d.venue } : {}),
    characters: [d.characters[0], d.characters[1]],
    specials: d.specials,
    drivesPerPlayer: tutorial || practice ? 10 : d.drives,
    // Tutorial and practice: the human always gets the ball first and plays without wind.
    firstPossession: tutorial || practice ? 0 : firstPossession,
    wind: tutorial || practice ? 0 : windValue(d.wind, seed),
  });
  const p1Name = playerName.trim() || 'P1';
  const human1: SlotSetup =
    d.mode === 'local'
      ? {
          kind: 'human',
          name: p1Name,
          character: d.characters[0],
          deviceIds: [DEVICE_ID.KB_P1, DEVICE_ID.pad(0)],
        }
      : {
          kind: 'human',
          name: p1Name,
          character: d.characters[0],
          deviceIds: [DEVICE_ID.KB_SOLO, DEVICE_ID.ANY_PAD, DEVICE_ID.TOUCH],
        };
  const slot2: SlotSetup =
    d.mode === 'local'
      ? {
          kind: 'human',
          name: 'P2',
          character: d.characters[1],
          deviceIds: [DEVICE_ID.KB_P2, DEVICE_ID.pad(1)],
        }
      : {
          kind: 'cpu',
          name: 'CPU',
          character: d.characters[1],
          difficulty: tutorial || practice ? Difficulty.EASY : d.difficulty,
        };
  return { mode: d.mode, config, slots: [human1, slot2] };
};
